import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, MailOpen, Eye, Paperclip, Search, RefreshCcw, Loader2, X, Download, Trash2 } from "lucide-react";

const KIND_LABEL = {
  proposal_send: "Devis",
  proposal_send_cc: "Devis (copie)",
  invoice_send: "Facture",
  invoice_reminder: "Relance facture",
  welcome: "Bienvenue",
  appointment: "RDV",
  other: "Autre",
};

const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function AdminEmailsBackup() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [active, setActive] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/emails", { params: kindFilter ? { kind: kindFilter, limit: 500 } : { limit: 500 } })
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [kindFilter]);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return rows;
    return rows.filter((r) => [r.to, r.subject, r.kind, KIND_LABEL[r.kind]].filter(Boolean).join(" ").toLowerCase().includes(k));
  }, [rows, q]);

  const openDetail = async (r) => {
    setActive(r); setActiveDetail(null);
    try {
      const d = await api.get(`/admin/emails/${r.id}`);
      setActiveDetail(d.data);
    } catch { toast.error("Impossible de charger l'email"); setActive(null); }
  };

  const delLog = async (id) => {
    if (!window.confirm("Supprimer définitivement cet email de l'archive ?")) return;
    try { await api.delete(`/admin/emails/${id}`); toast.success("Supprimé"); load(); if (active?.id === id) { setActive(null); setActiveDetail(null); } }
    catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-emails-backup">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Archives Emails</h1>
          <p className="text-sm text-[#64748B] mt-1">Tous les emails sortants — contenu HTML, pièces jointes et statut d'ouverture par le destinataire.</p>
        </div>
        <Button onClick={load} variant="outline" size="sm"><RefreshCcw size={13} className="mr-1.5" />Rafraîchir</Button>
      </div>

      <div className="mt-4 bg-white border border-black/8 rounded-2xl p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <Input data-testid="emails-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher destinataire, sujet…" className="pl-9" />
        </div>
        <select data-testid="emails-filter-kind" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="border border-black/10 rounded-md px-3 py-2 text-sm bg-white">
          <option value="">Tous types</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <span className="text-xs text-[#64748B]">{filtered.length} / {rows.length}</span>
      </div>

      <div className="mt-4 bg-white border border-black/8 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#64748B]"><Loader2 className="inline animate-spin mr-2" size={14} />Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Aucun email archivé.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-widest text-[#64748B]">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Destinataire</th>
                <th className="text-left px-3 py-2">Sujet</th>
                <th className="text-center px-3 py-2">PJ</th>
                <th className="text-center px-3 py-2">Statut</th>
                <th className="text-center px-3 py-2">Ouvert</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-black/5 hover:bg-[#FAFAFA]" data-testid={`email-row-${r.id}`}>
                  <td className="px-3 py-2 text-[#64748B] text-xs tabular-nums">{fmtDate(r.sent_at)}</td>
                  <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#06B6D4]/15 text-[#06B6D4] font-bold">{KIND_LABEL[r.kind] || r.kind}{r.test_mode ? " · TEST" : ""}</span></td>
                  <td className="px-3 py-2 text-[#0A1128] font-semibold">{r.to}</td>
                  <td className="px-3 py-2 text-[#0A1128] max-w-[300px] truncate">{r.subject}</td>
                  <td className="px-3 py-2 text-center">{(r.attachments?.length || 0) > 0 ? <span className="inline-flex items-center gap-1 text-[#64748B]"><Paperclip size={11} />{r.attachments.length}</span> : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {r.sent ? <span className="text-[10px] font-bold uppercase bg-[#A7F3D0] text-[#065F46] px-2 py-0.5 rounded-full">Envoyé</span>
                            : <span className="text-[10px] font-bold uppercase bg-[#FEE2E2] text-[#B91C1C] px-2 py-0.5 rounded-full">Échec</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.opened_at ? (
                      <span title={`Ouvert ${fmtDate(r.opened_at)}${r.open_count > 1 ? ` (${r.open_count}×)` : ""}`} className="inline-flex items-center gap-1 text-[#065F46] font-bold text-[11px]"><MailOpen size={12} />{r.open_count > 1 ? r.open_count + "×" : "Oui"}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#64748B] text-[11px]"><Mail size={12} />Non</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openDetail(r)} className="text-[#06B6D4] hover:text-[#0A1128] mr-2" data-testid={`email-view-${r.id}`} title="Voir"><Eye size={14} /></button>
                    <button onClick={() => delLog(r.id)} className="text-red-500 hover:text-red-700" data-testid={`email-del-${r.id}`} title="Supprimer"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {active && (
        <EmailDetail row={active} detail={activeDetail} onClose={() => { setActive(null); setActiveDetail(null); }} />
      )}
    </div>
  );
}

function EmailDetail({ row, detail, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1128]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold tracking-widest uppercase text-[#06B6D4]">{KIND_LABEL[row.kind] || row.kind}{row.test_mode ? " · APERÇU TEST" : ""}</div>
            <div className="display font-extrabold text-[#0A1128] text-base truncate">{row.subject}</div>
            <div className="text-xs text-[#64748B] mt-0.5">À <b>{row.to}</b> · {fmtDate(row.sent_at)}{row.opened_at ? ` · Ouvert ${fmtDate(row.opened_at)} (${row.open_count}×)` : " · Non ouvert"}</div>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0A1128] p-1"><X size={18} /></button>
        </div>
        {!detail ? (
          <div className="p-10 text-center"><Loader2 className="inline animate-spin text-[#06B6D4]" /></div>
        ) : (
          <div className="flex-1 overflow-auto">
            {(detail.attachments?.length || 0) > 0 && (
              <div className="px-6 py-3 border-b border-black/8 bg-[#FAFAFA] flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] mr-2"><Paperclip size={11} className="inline mr-1" />Pièces jointes</span>
                {detail.attachments.map((a, i) => (
                  <a key={i} href={`${window.location.origin}/api/admin/emails/${row.id}/attachments/${i}`} download={a.filename} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-black/10 bg-white text-xs hover:border-[#06B6D4] hover:text-[#06B6D4]"><Download size={11} />{a.filename}</a>
                ))}
              </div>
            )}
            <iframe srcDoc={detail.html} title="email" className="w-full h-[60vh] bg-white" sandbox="" />
          </div>
        )}
      </div>
    </div>
  );
}
